import { create } from 'zustand';
import { UserProfile as OriginalUserProfile } from '@/types';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

type TextSize = 'small' | 'medium' | 'large';
type ThemePreference = 'light' | 'dark' | 'system';

const getStorageItem = (key: string) => {
  if (!isBrowser) {
    return null;
  }

  return window.localStorage.getItem(key);
};

const setStorageItem = (key: string, value: string) => {
  if (!isBrowser) {
    return;
  }

  window.localStorage.setItem(key, value);
};

const removeStorageItem = (key: string) => {
  if (!isBrowser) {
    return;
  }

  window.localStorage.removeItem(key);
};

const prefersDarkMode = () => {
  if (!isBrowser) {
    return false;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const getThemePreference = (): ThemePreference | null => {
  const preference = getStorageItem('themePreference');
  return preference === 'light' || preference === 'dark' || preference === 'system' ? preference : null;
};

const getInitialDarkMode = () => {
  const storedPreference = getThemePreference();

  if (storedPreference === 'dark') {
    return true;
  }

  if (storedPreference === 'light') {
    return false;
  }

  return prefersDarkMode();
};

const getInitialTextSize = (): TextSize => {
  const storedTextSize = getStorageItem('textSize');
  return storedTextSize === 'small' || storedTextSize === 'medium' || storedTextSize === 'large'
    ? storedTextSize
    : 'medium';
};

const applyDarkModeClass = (darkMode: boolean) => {
  if (!isBrowser) {
    return;
  }

  document.documentElement.classList.toggle('dark', darkMode);
};

const applyTextSizeClass = (size: TextSize) => {
  if (!isBrowser) {
    return;
  }

  document.documentElement.classList.remove('text-size-small', 'text-size-medium', 'text-size-large');
  document.documentElement.classList.add(`text-size-${size}`);
};

export interface UserProfile extends Omit<OriginalUserProfile, 'defaultModelId'> {
  defaultModelApiName?: string;
}

interface AppState {
  user: UserProfile | null;
  setUser: (user: UserProfile | null) => void;
  darkMode: boolean;
  toggleDarkMode: (preference?: ThemePreference) => void;
  textSize: TextSize;
  setTextSize: (size: TextSize) => void;
  saveHistory: boolean;
  setSaveHistory: (save: boolean) => void;
  allowAnalytics: boolean;
  setAllowAnalytics: (allow: boolean) => void;
  ollamaBaseUrl: string | null;
  setOllamaBaseUrl: (url: string | null) => void;
  cohereKey: string | null;
  setCohereKey: (key: string | null) => void;
}

export const useAppStore = create<AppState>()((set, get) => ({
  user: null,
  setUser: (user) => set({ user }),
  darkMode: getInitialDarkMode(),

  toggleDarkMode: (preference) => {
    const currentDarkMode = get().darkMode;
    let newDarkMode: boolean;
    let newPreference: ThemePreference;

    if (preference === 'system') {
      newDarkMode = prefersDarkMode();
      newPreference = 'system';
    } else if (preference === 'dark') {
      newDarkMode = true;
      newPreference = 'dark';
    } else if (preference === 'light') {
      newDarkMode = false;
      newPreference = 'light';
    } else {
      newDarkMode = !currentDarkMode;
      newPreference = newDarkMode ? 'dark' : 'light';
    }

    set({ darkMode: newDarkMode });
    setStorageItem('themePreference', newPreference);
    applyDarkModeClass(newDarkMode);
  },

  textSize: getInitialTextSize(),
  setTextSize: (size) => {
    set({ textSize: size });
    setStorageItem('textSize', size);
    applyTextSizeClass(size);
  },
  saveHistory: getStorageItem('saveHistory') !== 'false',
  setSaveHistory: (save) => {
    set({ saveHistory: save });
    setStorageItem('saveHistory', save.toString());
  },
  allowAnalytics: getStorageItem('allowAnalytics') !== 'false',
  setAllowAnalytics: (allow) => {
    set({ allowAnalytics: allow });
    setStorageItem('allowAnalytics', allow.toString());
  },
  ollamaBaseUrl: getStorageItem('ollamaBaseUrl') || null,
  setOllamaBaseUrl: (url) => {
    set({ ollamaBaseUrl: url });
    setStorageItem('ollamaBaseUrl', url || '');
  },
  cohereKey: getStorageItem('cohere_api_key') || null,
  setCohereKey: (key) => {
    set({ cohereKey: key });
    if (key) {
      setStorageItem('cohere_api_key', key);
      return;
    }

    removeStorageItem('cohere_api_key');
  },
}));

if (isBrowser) {
  applyDarkModeClass(getInitialDarkMode());
  applyTextSizeClass(getInitialTextSize());

  if (getThemePreference() === 'system') {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      useAppStore.getState().toggleDarkMode('system');
    });
  }
}
